/**
 * useSurveyQuestions — survey-question library, a shared module store that
 * reuses the booking-question `Question` shape so the SAME QuestionEditorPage
 * edits both. Kept as its own library (distinct from booking questions): survey
 * questions are post-event feedback fields.
 *
 * Hydrates once from `listSurveyQuestions`; create/update/duplicate/delete
 * route through the real ops (createSurveyQuestion / updateSurveyQuestion /
 * deleteSurveyQuestion / duplicateSurveyQuestion, @v0y4ge/api-client), awaited
 * before the shared cache commits (UIR-09, 2026-07-25 round 2: the "no contract
 * op exists" premise was stale — the full family ships with a complete,
 * curated-seed-backed mock).
 *
 * Round 3 (2026-07-25, scrub-audit F6): a failed write now REJECTS instead of
 * silently committing a local-only record — phantom commits made a failed save
 * look identical to a successful one. Callers own the error state.
 */
import { useEffect, useState } from "react";
import { listSurveyQuestions, createSurveyQuestion, updateSurveyQuestion, deleteSurveyQuestion, duplicateSurveyQuestion } from "@v0y4ge/api-client";
import { mkOpt, type Question } from "../questions/useQuestions";
import { onTenantChange, onTenantSwitching } from "../../shell/access";

function toQuestion(q: Record<string, any>): Question {
  return {
    id: q.id,
    label: q.label ?? q.question ?? "",
    type: q.type ?? q.fieldType ?? "short",
    help: q.help ?? q.helpText ?? "",
    required: q.required ?? false,
    status: q.status === "approved" ? "approved" : "draft",
    owner: q.owner ?? q.createdBy ?? "—",
    updated: q.updatedAt ?? q.updated_at ?? "—",
    options: (q.options ?? []).map((o: any) => typeof o === "string" ? { id: o, label: o } : { id: o.id ?? o.value ?? o.label, label: o.label ?? o.value }),
  };
}

let _cache: Question[] | null = null;
let _loadStarted = false;
const _subs = new Set<() => void>();
const _emit = () => _subs.forEach((fn) => { try { fn(); } catch { /* noop */ } });
onTenantSwitching(() => { _cache = null; _loadStarted = false; _emit(); });
onTenantChange(() => { void _load(); });

async function _load(): Promise<void> {
  if (_loadStarted) return;
  _loadStarted = true;
  try {
    const r = await (typeof listSurveyQuestions === "function" ? listSurveyQuestions({} as any) : Promise.resolve(null));
    const arr = r ? ((r as any).questions ?? (r as any).data ?? []) : [];
    const fetched = (arr as any[]).map(toQuestion);
    const local = (_cache ?? []).filter((q) => !fetched.some((f) => f.id === q.id));
    _cache = fetched.length ? [...local, ...fetched] : (_cache ?? []);
    _emit();
  } catch {
    _cache = _cache ?? [];
    _emit();
  }
}

let _seq = 0;
const tmpId = (p: string) => `${p}_${Date.now().toString(36)}${(_seq++).toString(36)}`;

/** Create or update a survey question — awaits the real op, then commits the
 *  shared cache. Rejects (and commits nothing) when the op fails. */
export async function upsertSurveyQuestion(q: Question): Promise<void> {
  const cur = _cache ?? [];
  const isNew = !cur.some((x) => x.id === q.id);
  const opts = q.options.filter((o) => o.label.trim()).map((o) => ({ id: o.id, label: o.label }));
  const input = { label: q.label, type: q.type, help: q.help, required: q.required, status: q.status, options: opts };
  if (isNew) {
    const res = await createSurveyQuestion(input as any);
    _cache = [toQuestion((res as any).question ?? res), ...cur];
  } else {
    const res = await updateSurveyQuestion(q.id, input as any);
    const saved = toQuestion((res as any).question ?? res);
    _cache = cur.map((x) => (x.id === q.id ? saved : x));
  }
  _emit();
}

export function useSurveyQuestions() {
  const [, force] = useState(0);
  useEffect(() => {
    const cb = () => force((n) => n + 1);
    _subs.add(cb);
    void _load();
    return () => { _subs.delete(cb); };
  }, []);

  return {
    questions: (): Question[] => _cache ?? [],
    loading: _cache === null,
    getQ: (id: string): Question | null => (_cache ?? []).find((q) => q.id === id) ?? null,
    newQuestion: (): Question => ({ id: tmpId("sq"), label: "", type: "short", help: "", required: false, status: "draft", owner: "You", updated: "Just now", options: [] }),
    newOption: (label: string) => mkOpt(label),
    upsertQ: (q: Question) => upsertSurveyQuestion(q),
    duplicateQ: async (id: string): Promise<Question | null> => {
      const src = (_cache ?? []).find((x) => x.id === id);
      if (!src) return null;
      const res = await duplicateSurveyQuestion(id);
      const copy = toQuestion((res as any).question ?? res);
      _cache = [copy, ...(_cache ?? [])];
      _emit();
      return copy;
    },
    removeQ: async (id: string): Promise<void> => {
      await deleteSurveyQuestion(id);
      _cache = (_cache ?? []).filter((x) => x.id !== id);
      _emit();
    },
    usageOf: (_id: string) => 0,
    reload: () => { _loadStarted = false; void _load(); },
  };
}
